<template>
  <div v-if="status" class="mb-6">
    <!-- Rejected: Show feedback -->
    <div
      v-if="status.status === 'afgekeurd'"
      class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4"
    >
      <div class="flex items-start">
        <svg
          class="h-5 w-5 text-red-400 dark:text-red-500 mt-0.5 mr-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
          />
        </svg>
        <div class="flex-1">
          <h3 class="text-sm font-medium text-red-800 dark:text-red-300">
            Afgekeurd door {{ status.reviewed_by_name || 'Performance Lead' }}
          </h3>
          <p v-if="status.review_feedback" class="mt-1 text-sm text-red-700 dark:text-red-400">
            {{ status.review_feedback }}
          </p>
          <WorkflowFeedbackHistory :prospect-id="status.prospect_id" />
          <p class="mt-2 text-xs text-red-600 dark:text-red-400">
            Pas de voorkeuren aan en dien opnieuw in.
          </p>
        </div>
      </div>
    </div>

    <!-- Submitted: No banner needed - badge in header is sufficient -->

    <!-- Approved: Ready for presourcing -->
    <div
      v-else-if="status.status === 'goedgekeurd'"
      class="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4"
    >
      <div class="flex items-center">
        <svg
          class="h-5 w-5 text-green-400 dark:text-green-500 mr-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>
        <div>
          <h3 class="text-sm font-medium text-green-800 dark:text-green-300">
            Gematched! Klaar voor pre-sourcing
          </h3>
          <p class="text-xs text-green-600 dark:text-green-400">
            Gematched door {{ status.reviewed_by_name || 'Performance Lead' }}
          </p>
        </div>
      </div>
    </div>

    <!-- Heraanvraag: PL wants role adjustments -->
    <div
      v-else-if="status.status === 'heraanvraag'"
      class="bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-lg p-4"
    >
      <div class="flex items-start">
        <svg
          class="h-5 w-5 text-purple-400 dark:text-purple-500 mt-0.5 mr-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182"
          />
        </svg>
        <div class="flex-1">
          <h3 class="text-sm font-medium text-purple-800 dark:text-purple-300">
            Heraanvraag door {{ status.heraanvraag_by_name || 'Performance Lead' }}
          </h3>
          <p
            v-if="status.heraanvraag_feedback"
            class="mt-1 text-sm text-purple-700 dark:text-purple-400"
          >
            {{ status.heraanvraag_feedback }}
          </p>
          <WorkflowFeedbackHistory :prospect-id="status.prospect_id" />
          <p class="mt-2 text-xs text-purple-600 dark:text-purple-400">
            Pas de rollen/voorkeuren aan en dien opnieuw in.
          </p>
        </div>
      </div>
    </div>

    <!-- Te bespreken: Needs discussion -->
    <div
      v-else-if="status.status === 'te_bespreken'"
      class="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded-lg p-4"
    >
      <div class="flex items-start">
        <svg
          class="h-5 w-5 text-orange-400 dark:text-orange-500 mt-0.5 mr-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z"
          />
        </svg>
        <div class="flex-1">
          <h3 class="text-sm font-medium text-orange-800 dark:text-orange-300">Te bespreken</h3>
          <p
            v-if="status.review_feedback"
            class="mt-1 text-sm text-orange-700 dark:text-orange-400"
          >
            {{ status.review_feedback }}
          </p>
          <WorkflowFeedbackHistory :prospect-id="status.prospect_id" />
          <p class="mt-2 text-xs text-orange-600 dark:text-orange-400">
            Gemarkeerd door {{ status.reviewed_by_name || 'Performance Lead' }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import WorkflowFeedbackHistory from '@/components/common/feedback/WorkflowFeedbackHistory.vue'

defineProps({
  status: {
    type: Object,
    default: null
  }
})
</script>
